/**
 * Server data for the owner app. Everything is scoped by the X-Business-Id
 * header (auth.ts) and, where it applies, the branch picked in the header.
 */
import type {
  BranchDto,
  CategoryDto,
  DashboardDto,
  DeviceDto,
  EmployeeDto,
  ExpenseDto,
  NotificationsDto,
  RoleDto,
  NamedDto,
  Page,
  ProductDto,
  PurchaseInput,
  SaleDetailDto,
  SalesPageDto,
  StockLevelDto,
  SupplierDto,
} from '@pos/api-client';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { api, useAuth } from './auth';

const BRANCH_KEY = 'pos.owner.branch';

/** null = all branches. Remembered per phone. */
export const useBranch = create<{ branchId: string | null; set: (id: string | null) => void }>((set) => ({
  branchId: (() => {
    try {
      return localStorage.getItem(BRANCH_KEY);
    } catch {
      return null;
    }
  })(),
  set: (branchId) => {
    try {
      if (branchId) localStorage.setItem(BRANCH_KEY, branchId);
      else localStorage.removeItem(BRANCH_KEY);
    } catch {
      // storage blocked: the choice lasts for this visit
    }
    set({ branchId });
  },
}));

const biz = () => useAuth.getState().businessId;

/** The inbox; checked every minute while the app is open. */
export function useNotifications() {
  const businessId = useAuth((s) => s.businessId);
  return useQuery({
    queryKey: ['notifications', businessId],
    queryFn: () => api.get<NotificationsDto>('/notifications', { limit: 100 }),
    enabled: businessId !== null,
    refetchInterval: 60_000,
  });
}

export function useReadNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | 'all') => (id === 'all' ? api.post('/notifications/read-all') : api.post(`/notifications/${id}/read`)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useBranches() {
  return useQuery({ queryKey: ['branches', biz()], queryFn: () => api.get<BranchDto[]>('/branches'), staleTime: 5 * 60_000 });
}

export function useDashboard() {
  const branchId = useBranch((s) => s.branchId);
  return useQuery({
    queryKey: ['dashboard', biz(), branchId],
    queryFn: () => api.get<DashboardDto>('/dashboard', { branchId: branchId ?? undefined }),
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
  });
}

export type SalesPreset = 'today' | 'yesterday' | 'week' | 'month';

export function useSalesFeed(preset: SalesPreset, q: string) {
  const branchId = useBranch((s) => s.branchId);
  return useInfiniteQuery({
    queryKey: ['sales', biz(), branchId, preset, q],
    queryFn: ({ pageParam }) =>
      api.get<SalesPageDto>('/sales', { preset, q: q || undefined, branchId: branchId ?? undefined, cursor: pageParam ?? undefined, limit: 30 }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function useSale(id: string | undefined) {
  return useQuery({ queryKey: ['sale', id], queryFn: () => api.get<SaleDetailDto>(`/sales/${id}`), enabled: !!id });
}

export const STOCK_PAGE_SIZE = 100;

export function useStock(q: string, lowOnly: boolean, page = 1) {
  const branchId = useBranch((s) => s.branchId);
  return useQuery({
    queryKey: ['stock', biz(), branchId, q, lowOnly, page],
    queryFn: () =>
      api.get<Page<StockLevelDto>>('/stock/levels', {
        branchId: branchId ?? undefined,
        q: q || undefined,
        lowStock: lowOnly ? 'true' : undefined,
        page,
        pageSize: STOCK_PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });
}

export interface StockSummaryDto {
  branchId: string | null;
  stockValue: number;
  positions: number;
  lowCount: number;
  outCount: number;
}

/** The figures, counted by the database over the whole catalogue. */
export function useStockSummary(q: string, lowOnly: boolean) {
  const branchId = useBranch((s) => s.branchId);
  return useQuery({
    queryKey: ['stock-summary', biz(), branchId, q, lowOnly],
    queryFn: () =>
      api.get<StockSummaryDto>('/stock/levels/summary', {
        branchId: branchId ?? undefined,
        q: q || undefined,
        lowStock: lowOnly ? 'true' : undefined,
      }),
    placeholderData: keepPreviousData,
  });
}

/** One page of products: for search boxes that only need the first hits. */
export function useProducts(q: string, enabled = true) {
  return useQuery({
    enabled,
    queryKey: ['products', biz(), q],
    queryFn: () => api.get<Page<ProductDto>>('/products', { q: q || undefined, pageSize: PRODUCTS_PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });
}

export const PRODUCTS_PAGE_SIZE = 50;

/** The whole catalogue, page by page: `/products` is paged and reports the true total. */
export function useProductsPaged(q: string) {
  return useInfiniteQuery({
    queryKey: ['products-paged', biz(), q],
    queryFn: ({ pageParam }) => api.get<Page<ProductDto>>('/products', { q: q || undefined, page: pageParam, pageSize: PRODUCTS_PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (last, all) => (all.reduce((n, p) => n + p.items.length, 0) < last.total ? all.length + 1 : undefined),
    placeholderData: keepPreviousData,
  });
}

export function useProduct(id: string | undefined) {
  return useQuery({ queryKey: ['product', id], queryFn: () => api.get<ProductDto>(`/products/${id}`), enabled: !!id && id !== 'new' });
}

export const useCategories = () => useQuery({ queryKey: ['categories', biz()], queryFn: () => api.get<CategoryDto[]>('/categories'), staleTime: 5 * 60_000 });
export const useSuppliers = () => useQuery({ queryKey: ['suppliers', biz()], queryFn: () => api.get<SupplierDto[]>('/suppliers'), staleTime: 5 * 60_000 });
export const useBrands = () => useQuery({ queryKey: ['brands', biz()], queryFn: () => api.get<NamedDto[]>('/brands'), staleTime: 5 * 60_000 });
export const useEmployees = () => useQuery({ queryKey: ['employees', biz()], queryFn: () => api.get<EmployeeDto[]>('/employees') });
export const useRoles = () => useQuery({ queryKey: ['roles', biz()], queryFn: () => api.get<RoleDto[]>('/roles'), staleTime: 5 * 60_000 });
export const useDevices = () => useQuery({ queryKey: ['devices', biz()], queryFn: () => api.get<DeviceDto[]>('/devices') });

export function useExpenses() {
  const branchId = useBranch((s) => s.branchId);
  return useQuery({
    queryKey: ['expenses', biz(), branchId],
    queryFn: () => api.get<{ total: number; items: ExpenseDto[] }>('/expenses', { preset: 'month', branchId: branchId ?? undefined }),
  });
}

/** Looks a scanned or typed code up in the catalogue (barcode, variant, extra codes, scale labels). */
export async function lookupCode(code: string): Promise<{ product: ProductDto; variantId: string | null; quantity: number } | null> {
  try {
    return await api.get<{ product: ProductDto; variantId: string | null; quantity: number }>('/products/lookup', { code });
  } catch {
    return null;
  }
}

export function useReceive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PurchaseInput) => api.post<{ id: string; number: string }>('/purchases', input),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['stock'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
        qc.invalidateQueries({ queryKey: ['products'] }),
      ]);
    },
  });
}
